import { fieldConfig } from "@autoform/zod";
import * as z from "zod";
import { buildQualityChecksSchema } from "../common/qualityChecks";
import { zNumber, zUrl } from "../common/schemaHelpers";

// All quality-check toggles default to false for visionGlobal; partner-level
// overrides flip them on per deployment.
const ALL_QUALITY_CHECKS_OFF = {
	GeneralPlacementError: false,
	NotInsideCameraView: false,
	NotCloseEnough: false,
	NotBackSide: false,
	NotFrontSide: false,
	NotSteady: false,
	Slower: false,
	BlurryPhoto: false,
};

// ---------------------------------------------------------------------------
// IMEI Scan
// ---------------------------------------------------------------------------

const imeiScanSchema = z.object({
	zoomOnScan: z.boolean().default(false).describe("Zoom on Scan"),
});

// ---------------------------------------------------------------------------
// Grading — sub-schemas
// ---------------------------------------------------------------------------

const imageCroppingSchema = z.object({
	enabled: z.boolean().default(true).describe("Enabled"),
	horizontalMargin: zNumber().default(7).describe("Horizontal Margin"),
	verticalMargin: zNumber().default(2).describe("Vertical Margin"),
});

const movementDetectorSchema = z.object({
	accelerationThreshold: zNumber().default(6).describe("Acceleration Threshold"),
	rotationThreshold: zNumber().default(90).describe("Rotation Threshold"),
});

const manualCaptureSchema = z.object({
	enableCanvas: z.boolean().default(false).describe("Canvas Enabled"),
});

const blurDetectorSchema = z.object({
	roiScoreThreshold: zNumber().default(0).describe("ROI Score Threshold"),
	roiToExteriorRatioThreshold: zNumber().default(0).describe("ROI to Exterior Ratio Threshold"),
});

const qualityChecksSchema = z.object({
	auto: buildQualityChecksSchema(ALL_QUALITY_CHECKS_OFF).prefault({}).describe("Auto"),
	manual: buildQualityChecksSchema(ALL_QUALITY_CHECKS_OFF).prefault({}).describe("Manual"),
	webNative: buildQualityChecksSchema(ALL_QUALITY_CHECKS_OFF).prefault({}).describe("Web Native"),
});

// ---------------------------------------------------------------------------
// Grading
// ---------------------------------------------------------------------------

const gradingSchema = z.object({
	phoneDetector: z
		.enum(["ai", "marker", "hybrid"])
		.default("marker")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description: "Phone detection mech for mask display and frame",
			}),
		)
		.describe("Phone Detector"),
	requireStableConditionsForCaptureMs: zNumber()
		.default(900)
		.describe("Require Stable Conditions for x ms Before Capture"),
	closeEnoughScreenPercentage: zNumber().default(0.4).describe("Required Screen Coverage for Close Enough Check"),
	imageCropping: imageCroppingSchema.prefault({}).describe("Image Cropping Settings"),
	sideDetectionModelUrl: zUrl()
		.default("https://pandas-assets.s3.eu-central-1.amazonaws.com/ai-models/side-detection/v2/")
		.describe("Side Detection Model URL"),
	movementDetector: movementDetectorSchema.prefault({}).describe("Peer Sensor Movement Detector"),
	phoneDetectorConfidence: zNumber().default(0.2).describe("Phone Detector Required Confidence"),
	captureFlow: z
		.enum(["flat", "axis"])
		.default("flat")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description: "Predefined flows for capturing order/groups of phone angles.",
			}),
		)
		.describe("Capture Flow"),
	messageThrottlingInterval: zNumber().default(1000).describe("Message Throttling Interval"),
	faceBlurring: z.boolean().default(true).describe("Face Blurring"),
	bidirectionalFlow: z.boolean().default(false).describe("Bidirectional Tilt"),
	manualCapture: manualCaptureSchema.prefault({}).describe("Manual Capture Settings"),
	blurDetector: blurDetectorSchema.prefault({}).describe("Blur Detection Settings"),
	qualityChecks: qualityChecksSchema.prefault({}).describe("Image Quality Checks"),
});

// ---------------------------------------------------------------------------
// Camera Resolution
// ---------------------------------------------------------------------------

const cameraResolutionSchema = z.object({
	width: zNumber().default(2300).describe("Width"),
	height: zNumber().default(2000).describe("Height"),
});

// ---------------------------------------------------------------------------
// Realtime History (Ably)
// ---------------------------------------------------------------------------

const realtimeHistorySchema = z.object({
	initialFetchDelayMs: zNumber().default(0).describe("Initial Fetch Delay (ms)"),
	pollIntervalMs: zNumber().default(0).describe("Polling Interval (ms)"),
});

// ---------------------------------------------------------------------------
// Vision Global — top-level schema
// ---------------------------------------------------------------------------

export const visionGlobalShape = {
	imeiScan: imeiScanSchema.prefault({}).describe("IMEI Scan"),
	grading: gradingSchema.prefault({}).describe("Grading"),
	cameraResolution: cameraResolutionSchema.prefault({}).describe("Camera Resolution"),
	realtimeHistory: realtimeHistorySchema.prefault({}).describe("Realtime History (Ably)"),
};

export const visionGlobalSchema = z.object(visionGlobalShape);

export type VisionGlobalConfig = z.infer<typeof visionGlobalSchema>;
